'use client';

import { useEffect, useRef } from 'react';
import type { Map as LeafletMap } from 'leaflet';

export interface StoneAreaMapThumbnailProps {
  latitude: number;
  longitude: number;
  zoom?: number;
  /** 'dark' uses CartoDB Dark Matter, 'light' uses CartoDB Positron */
  variant?: 'dark' | 'light';
  className?: string;
}

export function StoneAreaMapThumbnail({
  latitude,
  longitude,
  zoom = 13,
  variant = 'dark',
  className = '',
}: StoneAreaMapThumbnailProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let cancelled = false;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !mapRef.current && !cancelled) {
          (async () => {
            const leaflet = await import('leaflet');
            // @ts-expect-error -- CSS import handled by bundler
            await import('leaflet/dist/leaflet.css');

            if (cancelled || mapRef.current) return;

            const map = leaflet.map(el, {
              center: [latitude, longitude],
              zoom,
              zoomControl: false,
              attributionControl: false,
              dragging: false,
              scrollWheelZoom: false,
              doubleClickZoom: false,
              touchZoom: false,
              boxZoom: false,
              keyboard: false,
            });

            const tileUrl = variant === 'light'
              ? 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png'
              : 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';

            leaflet.tileLayer(tileUrl, {
              maxZoom: 19,
              subdomains: 'abcd',
            }).addTo(map);

            mapRef.current = map;
            observerRef.current?.disconnect();
          })();
        }
      },
      { rootMargin: '200px' },
    );

    observerRef.current.observe(el);

    return () => {
      cancelled = true;
      observerRef.current?.disconnect();
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [latitude, longitude, zoom, variant]);

  return (
    <div className={`relative w-full h-full ${className}`}>
      <div ref={containerRef} className="absolute inset-0" />
      {variant === 'dark' && (
        <div className="absolute inset-0 bg-teal-900/20 pointer-events-none" />
      )}
    </div>
  );
}

export default StoneAreaMapThumbnail;
